import { capitalize, IconButton, MenuItem, Select, Stack, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { DeleteIcon } from "../../../../../assets/icons";
import { QueryAction } from "../../../../../common/FiltersFormEngine/queryReducer";
import { FilterItem } from "../../../../../common/FiltersFormEngine/types";
import { FilterContexType } from "../../../../layout/ViewHeader";
import FilterGroup from "../FilterGroup";
import FilterRow from "../FilterRow";

type Props = {
	item: FilterItem;
	depth?: number;
	canDelete?: boolean;
	isFirst: boolean;
	parentId: string;
	parentOperator?: "and" | "or";
	filterContext?: FilterContexType;
	dispatch: React.Dispatch<QueryAction>;
	readOnly?: boolean;
};

// decides how to render a node in the filter tree
// group → recursive container
// row → atomic filter rule
const FilterItemRenderer = ({
	item,
	depth = 0,
	canDelete = true,
	isFirst,
	parentId,
	parentOperator = "and",
	filterContext,
	dispatch,
	readOnly,
}: Props) => {
	const { t } = useTranslation("common");

	const handleToggleParentOperator = () => {
		if (readOnly) return;

		dispatch({
			type: "UPDATE_GROUP",
			nodeId: parentId,
			payload: {
				rowOperator: parentOperator === "and" ? "or" : "and",
			},
		});
	};

	const handleRemove = () => {
		if (readOnly || !canDelete) return;

		dispatch({
			type: "REMOVE_NODE",
			nodeId: item.value.id,
		});
	};

	const operatorControl = isFirst ? (
		<Select
			disabled={readOnly}
			size="small"
			sx={{
				width: "80px",
				minWidth: "80px",
				maxWidth: "80px",
			}}
			value={parentOperator}
			onChange={handleToggleParentOperator}
			renderValue={(selected) => <Typography>{String(selected).toUpperCase()}</Typography>}
		>
			<MenuItem value="and">And</MenuItem>
			<MenuItem value="or">Or</MenuItem>
		</Select>
	) : (
		<Typography
			sx={{
				width: "80px",
				minWidth: "80px",
				maxWidth: "80px",
				textAlign: "right",
			}}
			variant="body1"
		>
			{capitalize(parentOperator)}
		</Typography>
	);

	const deleteTitle =
		item.type === "group"
			? !canDelete
				? t("no delete last group")
				: t("delete group")
			: !canDelete
				? t("single row no delete")
				: t("delete row");

	return (
		<Stack direction="row" spacing={2} width="100%" alignItems="start">
			<Stack flex={1} minWidth={0}>
				{item.type === "row" ? (
					<FilterRow
						item={item.value}
						filterContext={filterContext}
						dispatch={dispatch}
						readOnly={readOnly}
					/>
				) : (
					<FilterGroup
						group={item.value}
						depth={depth}
						dispatch={dispatch}
						filterContext={filterContext}
						readOnly={readOnly}
					/>
				)}
			</Stack>

			<Stack direction="row" spacing={2} alignItems="center" justifyContent="end">
				{operatorControl}

				<Tooltip title={deleteTitle} placement={item.type === "group" ? "top" : "left"}>
					<span>
						<IconButton disabled={!canDelete || readOnly} onClick={handleRemove}>
							<DeleteIcon fontSize="small" color="secondary" />
						</IconButton>
					</span>
				</Tooltip>
			</Stack>
		</Stack>
	);
};

export default FilterItemRenderer;
